import React, { createContext, useContext, useState } from "react";
import { X, Phone, MessageSquare, ExternalLink, LifeBuoy } from "lucide-react";
import { CRISIS_RESOURCES } from "@/lib/crisisData";

const EmergencyContext = createContext({ open: () => {} });
export const useEmergency = () => useContext(EmergencyContext);

export const EmergencyProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return (
    <EmergencyContext.Provider value={{ open }}>
      {children}
      {isOpen && <EmergencyModal onClose={close} />}
    </EmergencyContext.Provider>
  );
};

const EmergencyModal = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Immediate support lines"
      data-testid="emergency-modal"
    >
      <div className="absolute inset-0 bg-bsn-midnight/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-bsn-parchment rounded-t-3xl sm:rounded-2xl card-tactile">
        <div className="sticky top-0 bg-bsn-midnight text-bsn-parchment px-6 py-5 flex items-start justify-between gap-4 rounded-t-3xl sm:rounded-t-2xl">
          <div className="flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-bsn-gold shrink-0" />
            <div>
              <p className="eyebrow text-bsn-gold">You are not alone</p>
              <h2 className="font-serif text-2xl font-semibold">Immediate Support Lines</h2>
              <p className="text-xs text-bsn-ice/70 mt-0.5">Free · Confidential · 24 / 7 / 365</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="emergency-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          {CRISIS_RESOURCES.map((r) => (
            <div key={r.name} className="bg-white rounded-xl p-4 card-tactile">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <h3 className="font-serif text-lg font-semibold text-bsn-midnight">{r.name}</h3>
                <span
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full text-white shrink-0"
                  style={{ backgroundColor: r.accent }}
                >
                  {r.badge}
                </span>
              </div>
              <p className="text-sm text-bsn-sapphire/80 leading-relaxed mb-3">{r.description}</p>
              <div className="flex flex-wrap gap-2">
                {r.tel && (
                  <a
                    href={`tel:${r.tel}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg btn-gold transition-all"
                    data-testid={`emergency-call-${r.tel}`}
                  >
                    <Phone className="w-4 h-4" /> {r.telLabel || "Call"}
                  </a>
                )}
                {r.sms && (
                  <a
                    href={`sms:${r.sms}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-bsn-navy text-bsn-parchment hover:bg-bsn-sapphire transition-colors"
                    data-testid={`emergency-text-${r.sms}`}
                  >
                    <MessageSquare className="w-4 h-4" /> {r.smsLabel || "Text"}
                  </a>
                )}
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-bsn-navy text-bsn-parchment hover:bg-bsn-sapphire transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Find a Helpline
                  </a>
                )}
              </div>
            </div>
          ))}
          <p className="text-xs text-bsn-sapphire/70 text-center pt-2 leading-relaxed">
            If you or someone you love is in immediate danger, call <strong>911</strong> or go to the nearest
            emergency room. Bright Stories Network is a supportive community resource and is not a substitute for
            emergency or professional care.
          </p>
        </div>
      </div>
    </div>
  );
};
